import { AssetGroup } from '@play-co/timestep-core';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import View from '@play-co/timestep-core/lib/ui/View';
import StateObserver from 'src/StateObserver';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import SpinnerLocal from 'src/game/components/shared/SpinnerLocal';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { getScreenDimensions } from 'src/lib/utils';

/**
 * Game-owned reward bubble shown behind LINE's Add-Friend half-modal.
 */
export default class PopupIncentivizedOAOverlay extends PopupBasic<() => void> {
  private readonly spinner: SpinnerLocal;
  /** Reward label refreshed whenever the popup is initialized with a new offer. */
  private readonly amountLabel: LangBitmapFontTextView;

  /**
   * Create the blocking bubble and loading feedback for the LINE half-modal.
   *
   * @param opts Popup parent and close callback.
   */
  public constructor(opts: { superview: View; close: () => void }) {
    super({
      ...opts,
      skipBackground: true,
      skipTitle: true,
      skipMessage: true,
      closeableWithBg: false,
      zIndex: 10020,
      tracking: {
        feature: 'resubscribe',
        $subFeature: 'resubscribe-incentivized-oa-overlay',
      },
    });

    // The native half-modal owns dismissal, so the game layer has no close button.
    this.buttonClose.removeFromSuperview();

    // Consume all background input while LINE loads and remains open.
    this.bg.updateOpts({ backgroundColor: 'rgba(75,56,96,0.9)' });

    // Scale the supplied bubble to the game viewport without cropping it.
    const bubbleScale = Math.min(1, (this.box.style.width - 40) / 926);
    const bubbleWidth = 926 * bubbleScale;
    const bubbleHeight = 549 * bubbleScale;

    // Match the resubscribe overlay bubble position - moving this bubble a bit up because of its height.
    const bubbleY = getScreenDimensions().height * 0.35 - 220;
    const waitY = bubbleY + 280;
    const spinnerCenterY = waitY + 150;

    const bubble = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/incentivizedOA/bubble.png',
      width: bubbleWidth,
      height: bubbleHeight,
      x: this.box.style.width / 2,
      y: bubbleY,
      centerOnOrigin: true,
      centerAnchor: true,
    });

    // Move the offer copy down to match the supplied bubble composition.
    const bubbleContentOffsetY = 30;
    const subscriptionTextOffsetX = StateObserver.getLocale() === 'ja' ? 0 : 5;
    const subscriptionTextX = bubbleWidth * 0.31 + subscriptionTextOffsetX;
    const subscriptionTextWidth = bubbleWidth * 0.6;
    const rewardCenterX = subscriptionTextX + subscriptionTextWidth / 2;

    // State the OA requirement on its own line inside the blank bubble area.
    new LangBitmapFontTextView({
      superview: bubble,
      x: subscriptionTextX,
      y: bubbleHeight * 0.35 + bubbleContentOffsetY,
      width: subscriptionTextWidth,
      height: 58,
      align: 'center',
      verticalAlign: 'center',
      size: 38,
      color: '#91808C',
      font: 'Body',
      wordWrap: true,
      canHandleEvents: false,
      localeText: () => i18n('incentivizedOA.overlay.message'),
    });

    // Center the reward number and gem as one row beneath the subscription phrase.
    const amountVisualWidth = 100;
    const amountLabelWidth = 100;
    const amountGemGap = 10;
    const gemSize = 62;
    const rewardRowWidth = amountVisualWidth + amountGemGap + gemSize;
    const rewardRowLeftX = rewardCenterX - rewardRowWidth / 2;

    // Align the number against the gem so their shared row stays centered.
    this.amountLabel = new LangBitmapFontTextView({
      superview: bubble,
      x: rewardRowLeftX + amountVisualWidth - amountLabelWidth / 2,
      y: bubbleHeight * 0.62 + bubbleContentOffsetY,
      width: amountLabelWidth,
      height: 70,
      centerOnOrigin: true,
      align: 'right',
      verticalAlign: 'center',
      size: 46,
      color: '#91808C',
      font: 'Body',
      canHandleEvents: false,
    });

    // Place a larger, aspect-preserving gem icon beside the reward amount.
    new ImageScaleView({
      superview: bubble,
      image: 'assets/ui/hud/icons/hud_gem.png',
      width: gemSize,
      height: gemSize,
      x: rewardRowLeftX + amountVisualWidth + amountGemGap + gemSize / 2,
      y: bubbleHeight * 0.62 + bubbleContentOffsetY,
      centerOnOrigin: true,
      centerAnchor: true,
      scaleMethod: 'contain',
    });

    // Keep all new treatment typography on the Body font.
    new LangBitmapFontTextShadowView({
      superview: this.box,
      x: this.box.style.width / 2,
      y: waitY,
      width: 540,
      height: 62,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 42,
      color: '#FFFDA1',
      textShadowColor: 'rgba(0,0,0,0.25)',
      textShadowOffsetY: 4,
      font: 'Body',
      canHandleEvents: false,
      localeText: () => i18n('incentivizedOA.overlay.loading'),
    });

    // Show local loading feedback below the reward bubble.
    this.spinner = new SpinnerLocal({
      superview: this.box,
      size: 118,
      offset: {
        x: 0,
        y: spinnerCenterY - this.box.style.height / 2,
      },
    });
  }

  /**
   * Return all image assets needed before the LINE half-modal opens.
   *
   * @returns Asset group for the bubble, gem icon, and spinner.
   */
  protected getAssetsGroup(): AssetGroup {
    return new AssetGroup([
      'assets/ui/incentivizedOA/bubble.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/shared/icons/loading_spinner.png',
    ]);
  }

  /** Initialize the overlay through the standard popup lifecycle. */
  public override init(args: { amount: number }): void {
    // Set the offered amount before the popup attaches and refreshes its text.
    this.amountLabel.localeText = () => args.amount.toString();
    // Let PopupBasic attach, load, and animate the overlay.
    super.init({});

    // Start the local spinner while the native prompt is active.
    this.spinner.start();
  }
}
